import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Sparkles,
  Award,
  Mail,
  Linkedin,
  Star,
  ChevronLeft,
  ChevronRight,
  Lock,
} from "lucide-react";
import Aurora from "@/components/Aurora";
import { Hero, IdentitySection, StuckSection, PathsSection, SystemsCarousel, TiersSection, QuizSection } from "@/pages/LandingSections";

const SALES_VIDEO_URL =
  "https://pub-e4209e8384fe4db983eaa5f05c20e56b.r2.dev/ai-employees/videos/always-on-sales-ai-employees-masterclass-sales-video.mp4";

function SalesVideo() {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(video);

    const handleEnded = () => {
      video.muted = true;
      video.loop = true;
      video.currentTime = 0;
      setIsMuted(true);
      video.play().catch(() => {});
    };
    video.addEventListener("ended", handleEnded);

    return () => {
      observer.disconnect();
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  const handleClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted) {
      video.loop = false;
      video.currentTime = 0;
      video.muted = false;
      setIsMuted(false);
      video.play().catch(() => {});
    } else {
      video.muted = true;
      video.loop = true;
      setIsMuted(true);
    }
  }, []);

  return (
    <div
      className="relative aspect-video glass-card overflow-hidden cursor-pointer"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      aria-label="Watch the sales video — click to unmute"
      data-testid="hero-demo-video-placeholder"
    >
      <video
        ref={videoRef}
        src={SALES_VIDEO_URL}
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute bottom-3 right-3 pointer-events-none">
        <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
          {isMuted ? (
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}
import Nav from "@/components/Nav";
import LeadForm from "@/components/LeadForm";
import Reveal from "@/components/Reveal";
import ChatWidget from "@/components/ChatWidget";
import EnrollButton from "@/components/EnrollButton";
import {
  SECTION,
  MODULE,
} from "@/constants/testIds";

const SYSTEM_IMAGES = {
  card1:
    "https://customer-assets.emergentagent.com/job_ai-systems-5/artifacts/k0e36knq_favor-studio-ai-outreach-agent-dashboard-b2b-firms-new-york.PNG",
  card2:
    "https://customer-assets.emergentagent.com/job_ai-systems-5/artifacts/yxu2c410_AI-Content-Operating-System.PNG",
  card3:
    "https://customer-assets.emergentagent.com/job_ai-systems-5/artifacts/zx5zk12n_AI-Client-Acquisition-System-B2B-Firms.jpg",
  founder: "/founder/Founder.PNG",
};

const CORE_MODULES = [
  "Welcome & How This Works",
  "What Is an AI Agent",
  "AI Agent vs AI Workflow",
  "Identify Your Business Constraint",
  "The Briefing Framework",
  "Tools & Setup Checklist",
  "Security & Best Practices",
  "Stage 1 \u2014 Setup & Define",
  "Stage 2 \u2014 Build the Logic",
  "Stage 3 \u2014 Test & Debug",
  "Stage 4 \u2014 Deploy Employee",
  "Agent Dashboard",
  "Next Steps \u2014 Scale Your Systems",
];

const CERT_USES = [
  "Validate your skills to clients",
  "Add it to your LinkedIn",
  "Charge businesses to build their agents for them",
  "Get hired by companies building AI infrastructure",
];

const DM_SCREENSHOTS = [
  {
    src: "/receipts/job-offer.png",
    caption: "6-figure infra role + equity",
    source: "LINKEDIN",
  },
  {
    src: "/receipts/sponsorship.png",
    caption: "Paid AI sponsorship deal",
    source: "EMAIL",
  },
  {
    src: "/receipts/law-firm.png",
    caption: "Inbound law firm consulting",
    source: "DM",
  },
  {
    src: "/receipts/private-equity.png",
    caption: "Private equity opportunity",
    source: "LINKEDIN",
  },
  {
    src: "/receipts/acquisition.png",
    caption: "Unsolicited acquisition offer",
    source: "DM",
  },
];

const PROOF_SLIDES = [
  { src: "/receipts/acquisition.png",     tag: "DM",       title: "Unsolicited acquisition offer",      accent: "#D4AF37" },
  { src: "/receipts/job-offer.png",       tag: "LINKEDIN", title: "6-figure infra role + equity",       accent: "#00F0FF" },
  { src: "/receipts/law-firm.png",        tag: "DM",       title: "Inbound law firm consulting",        accent: "#00F0FF" },
  { src: "/receipts/private-equity.png",  tag: "DM",       title: "Private equity opportunity",         accent: "#00F0FF" },
  { src: "/receipts/sponsorship.png",     tag: "EMAIL",    title: "Paid AI sponsorship deal",           accent: "#D4AF37", mediaMaxHeight: 440 },
];

function ProofCarousel() {
  const trackRef = useRef(null);
  const sectionRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const hoveredRef = useRef(false);
  const draggingRef = useRef(false);
  const indexRef = useRef(0);
  useEffect(() => { indexRef.current = index; }, [index]);
  useEffect(() => { draggingRef.current = dragging; }, [dragging]);

  const programmaticScrollRef = useRef(false);
  const lastInteractionRef = useRef(0);
  const PAUSE_AFTER_INTERACTION_MS = 9000;

  const scrollToIndex = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[i];
    if (!card) return;
    setIndex(i);
    lastInteractionRef.current = Date.now();
    programmaticScrollRef.current = true;
    const left = card.offsetLeft - track.offsetLeft;
    track.scrollTo({ left, behavior: "smooth" });
    window.setTimeout(() => { programmaticScrollRef.current = false; }, 700);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf;
    const onScroll = () => {
      if (programmaticScrollRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let closestDist = Infinity;
        for (let i = 0; i < track.children.length; i++) {
          const c = track.children[i];
          const mid = c.offsetLeft + c.offsetWidth / 2 - track.offsetLeft;
          const d = Math.abs(mid - center);
          if (d < closestDist) { closestDist = d; closest = i; }
        }
        setIndex(closest);
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => { track.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    const onDown = (e) => {
      if (e.target.closest("a, button")) return;
      isDown = true;
      setDragging(true);
      track.classList.add("aos-grabbing");
      startX = (e.pageX || e.touches?.[0]?.pageX) - track.offsetLeft;
      startScroll = track.scrollLeft;
    };
    const onMove = (e) => {
      if (!isDown) return;
      const x = (e.pageX || e.touches?.[0]?.pageX) - track.offsetLeft;
      const walk = (x - startX) * 1.1;
      track.scrollLeft = startScroll - walk;
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      setDragging(false);
      track.classList.remove("aos-grabbing");
    };
    track.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    track.addEventListener("mouseleave", onUp);
    return () => {
      track.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      track.removeEventListener("mouseleave", onUp);
    };
  }, []);

  const total = PROOF_SLIDES.length;
  const prev = () => scrollToIndex(Math.max(0, index - 1));
  const next = () => scrollToIndex(Math.min(total - 1, index + 1));

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqDesktop = window.matchMedia("(min-width: 768px)");
    const mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mqDesktop.matches || mqReduced.matches) return;
    const id = setInterval(() => {
      if (draggingRef.current) return;
      if (Date.now() - lastInteractionRef.current < PAUSE_AFTER_INTERACTION_MS) return;
      const nextIdx = (indexRef.current + 1) % total;
      const track = trackRef.current;
      if (track) {
        const card = track.children[nextIdx];
        if (card) {
          programmaticScrollRef.current = true;
          const left = card.offsetLeft - track.offsetLeft;
          track.scrollTo({ left, behavior: "smooth" });
          window.setTimeout(() => { programmaticScrollRef.current = false; }, 700);
        }
      }
      setIndex(nextIdx);
    }, 4500);
    return () => clearInterval(id);
  }, [total]);

  return (
    <section
      ref={sectionRef}
      data-testid="proof-section"
      className="section-dark grain py-24 md:py-32 border-t border-white/5"
    >
      <div className="orb orb-blue" style={{ width: 620, height: 620, top: "-180px", left: "-200px" }} />
      <div className="orb orb-gold" style={{ width: 380, height: 380, bottom: "-140px", right: "-100px" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="aos-receipts-pill">
              <span className="aos-receipts-dot" />
              Receipts
            </span>
            <h2 className="font-display font-black text-white leading-[1.02] text-[clamp(2.25rem,5vw,4.25rem)] mt-5">
              Real inboxes.{" "}
              <span className="text-[#00F0FF]">Real offers.</span>
            </h2>
            <p className="text-white/65 text-base md:text-lg leading-relaxed mt-4 max-w-xl">
              Acquisition offers, six-figure job offers, paid sponsorships,
              inbound from law firms — pulled straight from the inbox. Swipe
              through.
            </p>
          </div>
        </div>

        <div
          ref={trackRef}
          data-testid="proof-track"
          className={`aos-proof-track ${dragging ? "aos-grabbing" : ""}`}
        >
          {PROOF_SLIDES.map((s, i) => (
            <ProofCard
              key={s.src}
              slide={s}
              index={i}
              total={total}
              active={i === index}
              onClick={() => scrollToIndex(i)}
              testid={`proof-card-${i}`}
            />
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-5">
          <CarouselArrow onClick={prev} disabled={index === 0} direction="left" testid="proof-prev" />
          <div className="flex items-center gap-2">
            {PROOF_SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`proof-dot-${i}`}
                onClick={() => scrollToIndex(i)}
                aria-label={`Show proof ${i + 1}`}
                className="aos-proof-dot"
                style={{
                  background: i === index ? "#00F0FF" : "rgba(255,255,255,0.18)",
                  transform: i === index ? "scale(1.15)" : "scale(1)",
                  boxShadow: i === index ? "0 0 0 4px rgba(0,240,255,0.12)" : "none",
                }}
              />
            ))}
          </div>
          <CarouselArrow onClick={next} disabled={index === total - 1} direction="right" testid="proof-next" />
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white/35">
          <Lock size={10} /> Real Inbounds · Names Removed for Privacy
        </div>
      </div>
    </section>
  );
}

function ProofCard({ slide, index, total, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className="aos-proof-card"
      style={{
        transform: active ? "scale(1)" : "scale(0.95)",
        filter: active ? "none" : "saturate(0.9)",
      }}
    >
      <div
        className="aos-proof-shell"
        style={{
          borderColor: active ? `${slide.accent}55` : "rgba(255,255,255,0.10)",
          boxShadow: active
            ? `0 24px 60px rgba(0,0,0,0.55), 0 0 40px ${slide.accent}20`
            : "0 12px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div className="aos-proof-header" />
        <div
          className="aos-proof-media"
          style={slide.mediaMaxHeight ? { maxHeight: slide.mediaMaxHeight, overflow: "hidden" } : undefined}
        >
          <img
            src={slide.src}
            alt={slide.title}
            loading="lazy"
            draggable={false}
            style={
              slide.mediaMaxHeight
                ? { width: "100%", height: slide.mediaMaxHeight, objectFit: "cover", objectPosition: "top center" }
                : undefined
            }
          />
        </div>
        <div className="aos-proof-meta">
          <span
            className="aos-proof-meta-tag"
            style={{
              color: slide.accent,
              borderColor: `${slide.accent}55`,
              background: `${slide.accent}14`,
            }}
          >
            {slide.tag}
          </span>
          <div className="aos-proof-meta-row">
            <div className="aos-proof-meta-title">{slide.title}</div>
            <div className="aos-proof-meta-index">{index + 1} / {total}</div>
          </div>
        </div>
      </div>
    </button>
  );
}

function CarouselArrow({ onClick, disabled, direction, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      aria-label={direction === "left" ? "Previous proof" : "Next proof"}
      className="aos-proof-arrow"
      style={{ opacity: disabled ? 0.3 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
    >
      {direction === "left" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
    </button>
  );
}

// ---------- Section: Modules ----------
function ModulesSection() {
  return (
    <section id="curriculum" className="relative py-24 sm:py-32 overflow-hidden" data-testid={SECTION.modules}>
      <Aurora variant="intense" rings />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="label-chip"><span className="dot" /><span>INSIDE THE COURSE</span></div>
        </Reveal>
        <Reveal delay={100} x={-40} blur={6}>
          <h2 className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-5xl text-white">
            <span className="font-normal">Build Your First AI Agent in the </span>
            <span className="font-bold">First 1&ndash;2 Days.</span>
          </h2>
        </Reveal>

        <Reveal delay={150}>
          <p className="mt-6 text-zinc-300 text-base sm:text-lg max-w-3xl leading-relaxed">
            This isn&apos;t a drawn-out program with filler modules. Sit through two short lessons and you&apos;re
            building your first agent. Every module after that layers on more: dashboards, tracking, debugging, and deployment.
          </p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-10">
          <div>
            <h3 className="text-xs tracking-[0.3em] uppercase text-cyan-300 mb-6">Core Modules</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {CORE_MODULES.map((m, i) => (
                <Reveal key={i} delay={30 * i}>
                  <div
                    data-testid={MODULE(i + 1)}
                    className="glass-card module-card p-5 h-full flex items-start gap-4"
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      e.currentTarget.style.setProperty("--x", `${e.clientX - rect.left}px`);
                      e.currentTarget.style.setProperty("--y", `${e.clientY - rect.top}px`);
                    }}
                  >
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 grid place-items-center shrink-0">
                      <span className="font-display text-cyan-300 font-bold text-sm">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <p className="text-white leading-snug pt-1.5 text-sm sm:text-base">{m}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs tracking-[0.3em] uppercase text-cyan-300 mb-6">Bonus Module</h3>
            <Reveal>
              <div
                data-testid={MODULE("bonus")}
                className="glass-card module-card p-7 sm:p-9 h-full glow-cyan relative overflow-hidden"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--x", `${e.clientX - rect.left}px`);
                  e.currentTarget.style.setProperty("--y", `${e.clientY - rect.top}px`);
                }}
              >
                <div className="aurora-blob aurora-cyan" style={{ width: 360, height: 360, top: -120, right: -120, opacity: 0.35 }} />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-5">
                    <Sparkles className="w-5 h-5 text-cyan-300" />
                    <span className="text-xs tracking-[0.3em] uppercase text-cyan-300">Bonus</span>
                  </div>
                  <h4 className="font-display text-2xl sm:text-3xl text-white leading-tight">
                    <span className="font-normal">Productize Your </span>
                    <span className="font-bold">AI Agents.</span>
                  </h4>
                  <p className="mt-5 text-zinc-300 leading-relaxed">
                    Learn how to turn your new AI skills into an additional revenue stream by
                    packaging, pricing, and selling AI agents to businesses that need them.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {["Pricing Frameworks", "Sales Process", "Delivery SOPs", "Client Onboarding"].map((t) => (
                      <span key={t} className="text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border border-cyan-400/25 text-cyan-200 bg-cyan-400/5">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="mt-6 glass-card p-7">
                <p className="text-zinc-300 leading-relaxed">
                  By the end of this course you will have a live, deployed AI agent running
                  you can show and sell. <span className="text-white">Not a demo. Not a simulation.</span>{" "}
                  A real system that works while you sleep.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Founder ----------
function FounderSection() {
  const credentials = [
    "Built 50+ agentic systems, including integrated systems running real client work.",
    "Screened 30+ AI developers for my agency.",
    "Train my agency\u2019s AI developers on my own SOPs for client implementation.",
    "Lead a team of sales people and developers inside my AI agency.",
    "A six-figure acquisition offer for my agency — six months in.",
    "A private equity offer to join a company as an AI systems builder.",
    "A six-figure job offer from a startup to build AI infrastructure.",
    "Inbound consulting requests from law firms.",
    "Paid sponsorship opportunities from AI companies.",
  ];

  return (
    <section id="about" className="relative pt-24 pb-36 sm:pt-32 sm:pb-48 overflow-hidden border-b border-white/[0.07]" data-testid={SECTION.founder}>
      <Aurora variant="side" streaks />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">

        {/* Eyebrow row */}
        <Reveal>
          <div className="flex flex-wrap items-center gap-3">
            <div className="label-chip"><span className="dot" /><span>FOUNDER &middot; FAVOR STUDIO</span></div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hidden sm:inline">
              Brought to you by Favor Studio
            </span>
          </div>
        </Reveal>

        <Reveal delay={100} x={32} blur={6}>
          <h2 className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-5xl text-white">
            <span className="font-normal">You&apos;re Not Learning From Someone Who Watched </span>
            <span className="font-bold">YouTube Videos.</span>
          </h2>
        </Reveal>

        <Reveal delay={150}>
          <p className="mt-4 text-cyan-300 text-lg sm:text-xl font-medium">
            From zero to six-figure offers in six months.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-5 gap-12 lg:gap-20 items-start">

          {/* Photo column */}
          <Reveal delay={200} className="lg:col-span-2">
            <div className="relative flex flex-col lg:sticky lg:top-28">
              <div className="aspect-[4/5] glass-card overflow-hidden relative">
                <img
                  src={SYSTEM_IMAGES.founder}
                  alt="Lovefavor Alexander — Founder of Favor Studio"
                  className="absolute inset-0 w-full h-full object-cover object-center opacity-95"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute bottom-3 right-3">
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold text-white"
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)", backdropFilter: "blur(12px)" }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                    Founder &middot; Favor Studio
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Content column */}
          <div className="lg:col-span-3 space-y-5 text-zinc-300 text-base sm:text-lg leading-relaxed">

            <Reveal delay={250}>
              <p>
                I&apos;m <span className="text-white font-semibold">Lovefavor Alexander</span> &mdash; founder
                and CEO of <span className="text-cyan-300">Favor Studio</span>, an AI infrastructure
                agency based in New York. I build and deploy custom AI systems for B2B companies and
                law firms, including AI employees that run autonomously inside my own business every
                single day.
              </p>
            </Reveal>

            <Reveal delay={300}>
              <p>
                Before I ever taught a class, I invested over four figures into learning AI, studied
                the frameworks, and built real production systems inside my own business first.
              </p>
            </Reveal>

            {/* Credentials checklist */}
            <Reveal delay={350} className="!mt-12">
              <ul className="space-y-6">
                {credentials.map((c, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-1 shrink-0 rounded-full flex items-center justify-center"
                      style={{ width: 24, height: 24, background: "rgba(0,240,255,0.12)", border: "1px solid rgba(0,240,255,0.5)" }}
                    >
                      <Check style={{ width: 14, height: 14, color: "#00F0FF" }} strokeWidth={2.5} />
                    </span>
                    <span className="text-zinc-200 text-sm sm:text-base leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* Pull-quote card */}
            <Reveal delay={400} className="!mt-14">
              <blockquote className="relative rounded-xl p-9 sm:p-12 overflow-hidden" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.14)", backdropFilter: "blur(16px)", boxShadow: "0 8px 40px rgba(0,0,0,0.45), 0 2px 10px rgba(0,0,0,0.3)" }}>
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-cyan-400/50" />
                <p className="italic text-zinc-300 text-sm sm:text-base leading-[1.9]">
                  &ldquo;These weren&apos;t lucky breaks. They happened because my AI systems made my agency
                  look like it had been operating for six years &mdash; when it had only been operating for
                  six months. I invested over four figures in AI education before I ever built a single
                  system for other businesses. I studied the frameworks, built the systems, got real
                  results &mdash; and then the offers started coming in.&rdquo;
                </p>
              </blockquote>
            </Reveal>

          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Certification ----------
function CertificationSection() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid={SECTION.certification}>
      <Aurora variant="default" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="label-chip"><span className="dot" /><span>WHAT YOU EARN</span></div>
        </Reveal>
        <Reveal delay={100} x={32} blur={6}>
          <h2 className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-4xl text-white">
            <span className="font-normal">Certified. By Favor Studio </span>
            <span className="font-bold">Agency.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-5 gap-10 items-stretch">
          <Reveal delay={150} className="lg:col-span-3">
            <div className="space-y-5 text-zinc-400 text-base sm:text-lg leading-relaxed">
              <p>
                Upon completing the course you receive an official{" "}
                <span className="text-white">AI Agents Certification</span> from Favor Studio Agency
                &mdash; proving you have the skills to build and deploy autonomous AI systems.
              </p>
              <p className="text-cyan-300">Use it to:</p>
              <ul className="space-y-3">
                {CERT_USES.map((c, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-cyan-300 mt-1 shrink-0">&mdash;</span>
                    <span className="text-zinc-200">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={250} className="lg:col-span-2">
            <div className="glass-card p-8 sm:p-10 h-full flex flex-col items-center text-center relative overflow-hidden">
              <div className="aurora-blob aurora-cyan" style={{ width: 300, height: 300, top: -100, left: "50%", transform: "translateX(-50%)", opacity: 0.4 }} />
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_40px_rgba(0,229,255,0.6)] mb-6 mx-auto">
                  <Award className="w-9 h-9 text-black" strokeWidth={2.2} />
                </div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-cyan-300">Certified</p>
                <h4 className="font-display text-white text-2xl mt-3 leading-tight">
                  <span className="font-normal">AI Agents </span>
                  <span className="font-bold">Builder.</span>
                </h4>
                <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
                  Awarded upon completion. Verifiable. Recognized by Favor Studio Agency.
                </p>
                <div className="flex justify-center gap-1 mt-5">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 text-cyan-300 fill-cyan-300" />)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Footer ----------
function Footer() {
  return (
    <footer className="relative pt-20 pb-12 overflow-hidden border-t border-cyan-500/10">
      <Aurora variant="subtle" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 shadow-[0_0_24px_rgba(0,229,255,0.5)]">
                <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
              </span>
              <span className="font-display text-white text-lg">
                <span className="font-normal">Favor</span>{" "}
                <span className="font-bold">Studio.</span>
              </span>
            </div>
            <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-xs">
              AI Infrastructure Agency &mdash; New York. Building autonomous AI systems for B2B firms.
            </p>
            <p className="mt-4 text-zinc-500 text-xs tracking-[0.2em] uppercase">
              <a href="https://favorstudio.agency/" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300 transition-colors">favorstudio.agency</a>
            </p>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase text-cyan-300 mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyan-300" />
                <a data-testid="footer-mail-support" href="mailto:contact@favorstudio.agency" className="hover:text-cyan-300 transition-colors">
                  contact@favorstudio.agency
                </a>
              </li>
              <li className="text-zinc-500 text-xs leading-relaxed mt-3">For support &mdash; contact@favorstudio.agency</li>
              <li className="text-zinc-500 text-xs leading-relaxed">For done-for-you AI systems &mdash; favorstudio.agency</li>
              <li className="text-zinc-500 text-xs leading-relaxed">For 1-on-1 coaching &mdash; contact@favorstudio.agency</li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase text-cyan-300 mb-4">Follow</h4>
            <div className="flex items-center gap-3">
              <a
                data-testid="footer-social-linkedin"
                href="https://www.linkedin.com/in/favor-studio-37821a37a?utm_source=share_via&utm_content=profile&utm_medium=member_ios"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-full border border-cyan-500/20 grid place-items-center text-zinc-300 hover:text-cyan-300 hover:border-cyan-400/60 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
            <RouterLink
              to="/stay-informed"
              className="inline-flex items-center gap-1.5 mt-5 text-xs tracking-[0.2em] uppercase text-zinc-500 hover:text-cyan-300 transition-colors group"
            >
              Stay Informed
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </RouterLink>
          </div>
        </div>

        <div className="mt-14 border-t border-cyan-500/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 slide-footer">
          <a href="https://favorstudio.agency/" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300 transition-colors">favorstudio.agency</a>
          <span className="text-center text-zinc-500 text-xs">&copy; 2026 Favor Studio Agency. All Rights Reserved.</span>
        </div>
      </div>
    </footer>
  );
}

// ---------- Main Landing ----------
export default function Landing() {
  return (
    <div className="bg-black text-white min-h-screen relative">
      <Nav />
      <main>
        <Hero video={<SalesVideo />} />
        <IdentitySection />
        <StuckSection />
        <PathsSection />
        <ModulesSection />
        <SystemsCarousel />
        <FounderSection />
        <ProofCarousel />
        <CertificationSection />
        <TiersSection />
        <QuizSection />
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
